import { useEffect } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, FileText, Users, CheckCircle, Clock, ChevronRight, ArrowUpRight } from "lucide-react";
import CTABand from "@/components/recruit/CTABand";

const PROCESS_STEPS = [
  {
    num: "01",
    icon: MessageCircle,
    title: "カジュアル面談",
    duration: "30～45分",
    format: "オンライン",
    desc: "まずはお互いを知る機会として、カジュアルにお話しします。ACOEの事業やこのポジションについてご説明し、あなたのキャリアや興味についてもお聞かせください。転職のご意向は問いません。",
    points: ["ACOEの事業とビジョンをご説明", "ポジションの詳細と期待値の共有", "あなたのキャリアと興味について", "質疑応答"]
  },
  {
    num: "02",
    icon: FileText,
    title: "適性検査",
    duration: "約30分",
    format: "オンライン",
    desc: "カジュアル面談後、正式に選考を進めたい場合は適性検査を受けていただきます。あなたの強みや適性を把握するためのものです。",
    points: ["オンラインで受検可能", "所要時間は約30分", "結果は面接時にフィードバック"]
  },
  {
    num: "03",
    icon: Users,
    title: "面接（1～2回）",
    duration: "呔45～60分",
    format: "オンライン or 対面",
    desc: "代表との面接を1～2回実施します。より深くお互いを理解し合う機会です。具体的な営業スタイルや成功体験、ACOEでどのように活躍したいかなど、率直にお話しできればと思います。",
    points: ["代表との1対1面接", "営業スタイルや成功体験のディスカッション", "ACOEでのビジョンと期待", "条件面のすり合わせ"]
  },
  {
    num: "04",
    icon: CheckCircle,
    title: "内定・入社",
    duration: "入社日は相談",
    format: "オファー面談",
    desc: "選考を通過された方には、正式なオファーをお出しします。条件や入社日については柔軟に相談可能です。現職の退職時期に合わせた調整も行います。",
    points: ["正式オファーのご提示", "条件・入社日の最終確認", "入社に向けた準備サポート"]
  },
];

const TIMELINE_INFO = [
  { label: "カジュアル面談～内定", value: "最短2週間" },
  { label: "面接回数", value: "1～2回" },
  { label: "入社時期", value: "相談可" },
];

const TIPS = [
  { title: "カジュアル面談は選考ではありません", desc: "転職を決めていなくても大丈夫です。ACOEを知る機会として、気軽にお申し込みください。" },
  { title: "準備は必要ありません", desc: "事前に何かを準備いただく必要はありません。リラックスしてお話しできればと思います。" },
  { title: "質問は大歓迎です", desc: "ACOEについて気になること、このポジションについて聞きたいことがあれば、何でもお聞きください。" },
];

export default function HiringProcessPage() {
  useEffect(() => { document.title = "採用の流れ | ACOE 採用サイト"; }, []);

  return (
    <div data-testid="process-page">
      {/* ─── HERO ─── */}
      <section className="pt-32 md:pt-40 pb-20 md:pb-28 bg-acoe-charcoal" data-testid="process-hero">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-gold font-heading font-semibold mb-6">Hiring Process</p>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.15] max-w-3xl">
            採用の流れ
          </h1>
          <p className="mt-8 text-white/60 text-base md:text-lg max-w-2xl leading-relaxed">
            ACOEの選考プロセスをご紹介します。カジュアル面談から内定まで、最短2週間。
          </p>
        </div>
      </section>

      {/* ─── TIMELINE SUMMARY ─── */}
      <section className="py-12 bg-acoe-charcoal border-t border-white/10" data-testid="process-timeline-summary">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-3 gap-6 md:gap-8">
            {TIMELINE_INFO.map((item, i) => (
              <div key={i} className="text-center" data-testid={`timeline-info-${i}`}>
                <p className="font-heading font-extrabold text-2xl md:text-3xl text-acoe-gold">{item.value}</p>
                <p className="text-sm text-white/50 mt-1">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PROCESS STEPS ─── */}
      <section className="py-20 md:py-32 bg-acoe-bg" data-testid="process-steps">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">Process</p>
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-acoe-charcoal mb-12">
            選考ステップ
          </h2>
          <div className="space-y-6 md:space-y-8">
            {PROCESS_STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={i} className="bg-white border border-acoe-border hover:border-acoe-charcoal transition-all duration-300" data-testid={`process-step-${i}`}>
                  <div className="p-6 md:p-10">
                    <div className="flex flex-col md:flex-row md:items-start gap-6">
                      <div className="flex items-center gap-4 md:w-64 flex-shrink-0">
                        <span className="font-heading font-extrabold text-4xl text-acoe-gold/30">{step.num}</span>
                        <div className="w-12 h-12 rounded-full bg-acoe-paper flex items-center justify-center">
                          <Icon size={22} className="text-acoe-charcoal" strokeWidth={1.5} />
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-4 mb-4">
                          <h3 className="font-heading font-bold text-xl text-acoe-charcoal">{step.title}</h3>
                          <div className="flex gap-2">
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-acoe-paper text-xs text-acoe-muted rounded-full">
                              <Clock size={12} strokeWidth={1.5} /> {step.duration}
                            </span>
                            <span className="px-3 py-1 bg-acoe-paper text-xs text-acoe-muted rounded-full">
                              {step.format}
                            </span>
                          </div>
                        </div>
                        <p className="text-acoe-text leading-relaxed mb-6">{step.desc}</p>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {step.points.map((point, j) => (
                            <li key={j} className="flex items-start gap-2">
                              <ChevronRight size={14} className="text-acoe-gold mt-1 flex-shrink-0" strokeWidth={2} />
                              <span className="text-sm text-acoe-text">{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                  {i < PROCESS_STEPS.length - 1 && (
                    <div className="flex justify-center pb-4">
                      <div className="w-px h-8 bg-acoe-border" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── TIPS ─── */}
      <section className="py-20 md:py-32 bg-acoe-paper" data-testid="process-tips">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">Tips</p>
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-acoe-charcoal mb-12">
            カジュアル面談について
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TIPS.map((tip, i) => (
              <div key={i} className="bg-white border border-acoe-border p-6 md:p-8" data-testid={`tip-${i}`}>
                <h3 className="font-heading font-bold text-base text-acoe-charcoal mb-3">{tip.title}</h3>
                <p className="text-sm text-acoe-text leading-relaxed">{tip.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section className="py-20 md:py-32 bg-acoe-charcoal" data-testid="process-final-cta">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-gold font-heading font-semibold mb-4">Get Started</p>
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-white mb-4">
            まずはカジュアル面談から
          </h2>
          <p className="text-white/50 mb-10 max-w-xl mx-auto">
            ACOEに少しでも興味をお持ちいただけたら、ぜひお話しさせてください。転職を決めていなくても大丈夫です。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/recruit/contact"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-acoe-gold text-acoe-charcoal px-8 py-4 font-heading font-semibold tracking-wide hover:bg-acoe-gold-hover transition-colors"
              data-testid="process-cta-primary"
            >
              お問い合わせ
              <ArrowUpRight size={18} strokeWidth={1.5} />
            </Link>
            <Link
              to="/recruit/careers"
              className="inline-flex items-center justify-center rounded-full border border-white/30 text-white px-8 py-4 font-heading font-semibold tracking-wide hover:bg-white/10 transition-all"
              data-testid="process-cta-secondary"
            >
              募集要項を見る
            </Link>
          </div>
        </div>
      </section>

      {/* ─── CTA BAND ─── */}
      <CTABand />
    </div>
  );
}
